import React, {useEffect, useMemo, useState} from 'react';
import CustomModal from "../../../components/UI/CustomModal";
import {
    Btn,
    EllipsisText,
    FlexColumnSection,
    FlexRowSection,
    FlexSection,
    Header,
    SmallHeader,
    SpanBold
} from "../../../styles/globalStyles";
import CustomCollapse from "../../../components/UI/CustomCollapse";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {useDispatch, useSelector} from "react-redux";
import {t} from "i18next";
import {
    getOneTimeMembershipType,
    getTrialMembershipType
} from "@arboxappv4/shared/src/helpers/functions";
import {membershipTypes} from "@arboxappv4/shared/src/helpers/constants";
import {
    getAttendanceDisplay,
    getPartialAttendanceDisplay,
    onLinkClicked,
    sortCourseEventDays
} from "../../../helpers/functions";
import dayjs from "dayjs";
import UserAvatar from "../../../components/UserAvatar";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {
    COURSE_MEMBERSHIP_MODAL,
    COURSE_PARTIAL_DAYS_MODAL,
    openModal,
    TRIAL_COURSE_SESSION_MODAL, updateModalRes
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {DB_DATE_FORMAT, DB_TIME_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import {useNavigate, useParams} from "react-router-dom";
import CustomFieldsDataDisplay from "../../../components/CustomFieldsDataDisplay";
import {getDisabledPagesCourseProps} from "@arboxappv4/shared/src/helpers/DisablePages";
import CourseAgeRestrictionDisplay from "../../../components/CourseAgeRestrictionDisplay";

const CourseMembershipSelect = (props) => {
    const { isOpen, onClose, course, onFullCourseSelected, onPartialBookingSelected } = props;
    const loggedIn = useSelector(state => state.auth.loggedIn)
    const globalLocation = useSelector(state => state.site.selectedLocation)
    const box = useSelector(state => state.site.box)
    const trialSessionRes = useSelector(state => state.modalManager[TRIAL_COURSE_SESSION_MODAL].processRes)
    const partialDaysRes = useSelector(state => state.modalManager[COURSE_PARTIAL_DAYS_MODAL].processRes)
    const [selectedPartialMembership, setSelectedPartialMembership] = useState(null)

    const {isMobile} = useScreenSize();
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const { id } = useParams();

    const currencySymbol = useMemo(() => globalLocation?.currency_symbol, [globalLocation]);
    const trial = useMemo(() => (course && !loggedIn) ? getTrialMembershipType(course) : null, [course])
    const onetime = useMemo(() => course ? getOneTimeMembershipType(course) : null, [course])
    const courseMT = useMemo(() => course && !course.isHugFull && !course.isBookedUser && !course.hide_full_hug_in_site_prop ? course.membership_types.find(item => ![membershipTypes.TRIAL, membershipTypes.ONETIME].includes(item.type)) : null, [course]);
    const partialOptions = useMemo(() => course && !course.isHugFull && !course.isBookedUser && course.partialBookingOptions.length > 0 ? course.partialBookingOptions : null, [course]);
    const isFullCourseOpen = useMemo(() => course && course.series_events.length === course.openDays.length, [course]);
    let days = useMemo(() => course ? sortCourseEventDays(course.series_events) : [], [course])
    const displayProps = useMemo(() => course ? getDisabledPagesCourseProps(course) : {}, [course])

    useEffect(() => {
        if(trialSessionRes) {
            const {session, type} = trialSessionRes
            const membershipType = type === 'trial' ? trial : onetime
            onFullCourseSelected(membershipType, session)
            dispatch(updateModalRes({modalName: TRIAL_COURSE_SESSION_MODAL, res: null}))
            onClose()
        }
    }, [trialSessionRes]);

    useEffect(() => {
        if(partialDaysRes) {
            dispatch(updateModalRes({modalName: COURSE_PARTIAL_DAYS_MODAL, res: null}))
            onPartialBookingSelected(selectedPartialMembership, partialDaysRes)
            onClose()
        }
    }, [partialDaysRes]);

    const getMembershipInfo = (membership) => {
        return (
            <FlexSection mobile={isMobile} justify={isMobile ? 'start' : membership.active === 2 ? 'space-between' : 'end'}>
                <EllipsisText width={isMobile ? 'auto' : '160px'}>
                    <span>{membership.price > 0 ? `${currencySymbol}${membership.price.toLocaleString()}` : t('free')}</span>
                </EllipsisText>
            </FlexSection>
        )
    }

    const onFullCourseSelect = () => {
        if(isFullCourseOpen) {
            onFullCourseSelected(courseMT)
            onClose()
        }
    }

    const onTrialSelected = (type = 'trial') => {
        dispatch(openModal({modalName: TRIAL_COURSE_SESSION_MODAL, props: {course: course, type}}))
    }

    const onPartialSelected = (option) => {
        if(option.canBook) {
            setSelectedPartialMembership(option)
            dispatch(openModal({modalName: COURSE_PARTIAL_DAYS_MODAL, props: {course: course, partialMembership: option}}))
        }
    }

    return (
        <CustomModal open={isOpen}
                     onClose={onClose}
                     width={isMobile ? 'inherit' : '800px'}
                     title={<Header>{course.box_categories.name}</Header>}
                     style={{body: {overflow: 'auto', height: isMobile ? 'calc(100% - 45px)' : 'auto'}}}
        >
            <FlexColumnSection gap={'15px'}>
                <FlexColumnSection gap={'10px'}>
                    <SmallHeader fontSize={'16px'}>{t('course-days-header')}</SmallHeader>
                    <span>
                    {
                        course.start_date === course.end_date ? t('one-day-hug', {start: dayjs(course.start_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})
                        :
                        course.end_date ?
                            `${dayjs(course.start_date, DB_DATE_FORMAT).format(globalLocation.date_format)} - ${dayjs(course.end_date, DB_DATE_FORMAT).format(globalLocation.date_format)}`
                            :
                            t('starts-at', {start: dayjs(course.start_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})
                    }
                    </span>
                    {days.map((day, i) => <FlexSection key={`day-${i}`} gap={isMobile ? '8px' : '15px'} flex={'unset'} mobile={isMobile}>
                        {t(`day-${day.day_of_week}`)}: {dayjs(day.start, DB_TIME_FORMAT).format(globalLocation.time_format)} - {dayjs(day.end, DB_TIME_FORMAT).format(globalLocation.time_format)}{day.description ? ` - ${day.description}` : ''}
                        {(displayProps.showNumOpenSpots || displayProps.showNumRegistered) &&
                            <>
                                {!isMobile && <FontAwesomeIcon icon={solid('circle-small')} color={Colors.siteBlack}
                                                  className={'dot-icon'}/>
                                }
                                <FlexSection gap={isMobile ? '8px' : '15px'} flex={'unset'} mobile={false}>
                                    <FontAwesomeIcon icon={solid('user')} color={Colors.siteBlack} size={'xs'}/>
                                    <span>{getPartialAttendanceDisplay(displayProps, course, day.id)}</span>
                                </FlexSection>
                            </>}
                    </FlexSection>)}
                    {displayProps.showNumRemainingSlots && course.end_date && <div>{t('remaining-slots', {remainingSlots: course.remainingSlots})}</div>}
                    {course.coach && <SmallHeader fontSize={'16px'}>{course.second_coach ? t('staff-members') : t('staff-member')}:</SmallHeader>}
                    {(course.coach || course.second_coach) && displayProps.showCoach &&
                        <FlexSection gap={isMobile ? '8px' : '15px'} flex={'unset'} mobile={isMobile}>
                            {course.coach && <UserAvatar user={course.coach} nameType={'full'}/>}
                            {course.second_coach && !isMobile && <FontAwesomeIcon icon={solid('circle-small')} color={Colors.siteBlack} className={'dot-icon'}/>}
                            {course.second_coach && <UserAvatar user={course.second_coach} nameType={'full'}/>}
                        </FlexSection>
                    }
                    <CustomFieldsDataDisplay data={course} />
                    <CourseAgeRestrictionDisplay course={course} />
                    {(box.locations_box.length > 1 || course.spaces) &&
                        <FlexSection gap={isMobile ? '8px' : '15px'} flex={'unset'} mobile={isMobile}>
                            {box.locations_box.length > 1 && <span><SpanBold>{t('location')}:</SpanBold> {course.locations_box.location}</span>}
                            {box.locations_box.length > 1 && course.spaces && !isMobile && <FontAwesomeIcon icon={solid('circle-small')} color={Colors.siteBlack} className={'dot-icon'}/>}
                            {course.spaces && <span><SpanBold>{t('space')}:</SpanBold> {course.spaces.name}</span>}
                        </FlexSection>
                    }
                </FlexColumnSection>
                <SmallHeader fontSize={'16px'} weight={'bold'}>{t('description')}:</SmallHeader>
                {course.box_categories.bio && <div style={{flex: 1, whiteSpace: 'pre-wrap'}}>{course.box_categories.bio}</div>}
                <FlexColumnSection gap={'5px'}>
                    <SmallHeader>{t('select-membership-header')}</SmallHeader>
                    <FlexColumnSection gap={'5px'} overflow={isMobile ? 'hidden' : 'hidden auto'} maxHeight={isMobile ? 'auto' : '190px'}>
                        {trial &&
                            <CustomCollapse
                                headerText={t('trial-hug-select-btn', {interpolation: {'escapeValue': false}})}
                                disableExpand={'icon'}
                                ctaText={t('purchase-btn')}
                                headerWidth={'auto'}
                                headerInfo={getMembershipInfo(trial)}
                                onBtnClick={onTrialSelected}
                                background={Colors.siteBg}
                            />}
                        {courseMT &&
                            <CustomCollapse
                                headerText={t('full-course-select-btn', {interpolation: {'escapeValue': false}})}
                                disableExpand={isFullCourseOpen ? 'icon' : 'disabled'}
                                ctaText={t(isFullCourseOpen ? 'purchase-btn' : 'full-course')}
                                headerWidth={'auto'}
                                ctaWidth={isFullCourseOpen ? 'auto' : '100px'}
                                headerInfo={getMembershipInfo(courseMT)}
                                onBtnClick={onFullCourseSelect}
                                background={Colors.siteBg}
                            />}
                        {partialOptions && partialOptions.map(option => (
                            <CustomCollapse
                                headerText={t(`partial-${option.numOfDays === 1 ? 'single' : 'multi-days'}-course-select-btn`, {num: t(`${option.numOfDays}-days`), interpolation: {'escapeValue': false}})}
                                disableExpand={option.canBook ? 'icon' : 'disabled'}
                                ctaText={t(option.canBook ? 'purchase-btn' : 'full-course')}
                                headerWidth={'auto'}
                                headerInfo={getMembershipInfo(option)}
                                onBtnClick={() => onPartialSelected(option)}
                                background={Colors.siteBg}
                                key={`key-partial-${option.numOfDays}`}
                                ctaWidth={option.canBook ? 'auto' : '100px'}
                            />
                        ))}
                        {onetime &&
                            <CustomCollapse
                                headerText={t('onetime-hug-select-btn', {interpolation: {'escapeValue': false}})}
                                disableExpand={'icon'}
                                ctaText={t('purchase-btn')}
                                headerWidth={'auto'}
                                headerInfo={getMembershipInfo(onetime)}
                                onBtnClick={() => onTrialSelected('onetime')}
                                background={Colors.siteBg}
                            />}
                    </FlexColumnSection>
                </FlexColumnSection>
            </FlexColumnSection>
        </CustomModal>
    )
};

export default CourseMembershipSelect;